import FileSaver from "file-saver";

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

import { getDefaultersData } from "../../redux/modules/defaulters/actions";

import {
  Container,
  FlexContainer,
  Search,
  Section,
  Title,
  GridAnimated,
  Warning,
  Option,
  OptionsPopover,
  PopoverTrigger,
  Indicator,
} from "./styles";
import Button from "./components/button";
import DefaultersTable from "./components/defaultersTable";
import { UploadSpreadSheet } from "./components/UploadSpreadsheet";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { FaCheck, FaSlidersH } from "react-icons/fa";
import { FiDownload, FiUpload } from "react-icons/fi";
import SearchIcon from "./components/Icons/searchIcon";

const DEFAULT_TABLE_OPTIONS = [
  "defaulters.table.sales_person",
  "defaulters.table.analysis_date",
  "defaulters.table.transaction_currency",
  "defaulters.table.customer_name",
  "defaulters.table.transaction_number",
  "defaulters.table.total",
  "defaulters.table.due",
  "defaulters.table.overdue",
  "defaulters.table.paid",
  "defaulters.table.opportunity",
  "actions",
];

export default function Defaulters() {
  const [upload, setUpload] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(DEFAULT_TABLE_OPTIONS);

  const intl = useIntl();
  const dispatch = useDispatch();
  const { data, isLoading, error } = useSelector((state) => state.defaulters);

  useEffect(() => {
    dispatch(getDefaultersData());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && !error && data.length === 0) {
      setUpload(true);
    }
  }, [data, error, isLoading]);

  const mappedData = useMemo(() => {
    if (inputValue === "") return data;

    const searchTerms = inputValue
      .toLowerCase()
      .split(";")
      .map((term) => term.trim())
      .filter(Boolean);

    return data.filter((item) => {
      const searchableFields = [
        item.salesPerson?.toLowerCase(),
        item.customerName?.toLowerCase(),
        item.transactionNumber?.toString().toLowerCase(),
        item.opportunity?.toLowerCase(),
      ];

      return searchTerms.some((term) =>
        searchableFields.some((field) => field?.includes(term))
      );
    });
  }, [data, inputValue]);

  const closeUpload = () => setUpload(false);

  const handleDownloadTemplate = () => {
    FileSaver.saveAs(
      `${process.env.PUBLIC_URL}/defaulters_template.xlsx`,
      "Defaulters_Template.xlsx"
    );
  };

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "breadcrumb.defaulters",
      })}
    >
      <Section>
        <Container>
          <FlexContainer spaceBetween>
            <div>
              <Title>
                <FormattedMessage id="defaulters.import-database" />
              </Title>

              <GridAnimated show={upload}>
                <Warning>
                  <span>
                    <FormattedMessage id="defaulters.data-format-requirement" />
                  </span>
                </Warning>
              </GridAnimated>
            </div>

            <FlexContainer width={"fit-content"} gap="8px">
              <Button
                label={"download_template"}
                variant={"tertiary"}
                Icon={<FiDownload />}
                shadow
                onClick={handleDownloadTemplate}
              />
              <Button
                label={"defaulters.import-data"}
                Icon={<FiUpload />}
                onClick={() => setUpload((prev) => !prev)}
                shadow
              />
            </FlexContainer>
          </FlexContainer>

          <GridAnimated show={upload}>
            <UploadSpreadSheet closeUpload={closeUpload} />
          </GridAnimated>
        </Container>

        <Container>
          <FlexContainer marginBottom={"24px !important"} spaceBetween>
            <Title>
              <FormattedMessage id="defaulters.defaulting-clients" /> (
              {mappedData.length})
            </Title>

            <FlexContainer gap="8px" width="fit-content">
              <Search
                size="large"
                placeholder={intl.formatMessage({ id: "search" })}
                prefix={<SearchIcon />}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <FilterCols items={options} setItems={setOptions} />
            </FlexContainer>
          </FlexContainer>

          <DefaultersTable
            data={mappedData}
            loading={isLoading}
            omitArray={options}
          />
        </Container>
      </Section>
    </AnimatedMainContainer>
  );
}

function FilterCols({ setItems, items }) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  return (
    <OptionsPopover
      placement="bottomRight"
      content={<Options setItems={setItems} items={items} />}
      title={intl.formatMessage({ id: "colsFilters" })}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger open={open}>
        <FaSlidersH />
        <Indicator show={DEFAULT_TABLE_OPTIONS.length !== items.length}>
          {DEFAULT_TABLE_OPTIONS.length - items.length}
        </Indicator>
      </PopoverTrigger>
    </OptionsPopover>
  );
}

function Options({ setItems, items }) {
  return (
    <div>
      {DEFAULT_TABLE_OPTIONS.map((i) => (
        <Option
          key={i}
          active={items.some((s) => s === i)}
          onClick={() => {
            setItems((prev) => {
              const newArr = [...prev];
              const isActive = newArr.some((s) => s === i);
              if (newArr.length === 1) return isActive ? newArr : [...prev, i];
              return isActive ? newArr.filter((a) => a !== i) : [...prev, i];
            });
          }}
        >
          <FaCheck />
          <span>
            <FormattedMessage id={i} />
          </span>
        </Option>
      ))}
      {items.length !== DEFAULT_TABLE_OPTIONS.length && (
        <Option onClick={() => setItems(DEFAULT_TABLE_OPTIONS)} reset>
          <span>
            <FormattedMessage id={"resetFilters"} />
          </span>
        </Option>
      )}
    </div>
  );
}
